import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatSortModule } from '@angular/material/sort';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { IonCheckbox, IonRow } from '@ionic/angular/standalone';
import { NgSelectModule } from '@ng-select/ng-select';
import { filter, map, switchMap, take } from 'rxjs';
import { SpecialCharacters } from 'src/app/core/enums';
import {
  BuildingScanAdmin,
  EnableDisable,
  UserAdmin,
} from 'src/app/core/models/admin';
import { BuildingScanService } from 'src/app/core/services/admin/building-scan/building-scan.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { DataService } from 'src/app/core/services/data/data.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { BANNER_TYPE } from '../../banner/banner.constants';
import { BUTTON_CLICK } from '../../shared/modal/exit-modal/exit-modal.constants';
import { IMAGE_SRC } from '../../shared/shared-constants';
import {
  COLUMN_TEXT,
  ColumnIds,
  CONSTS,
  EXCEL_COLUMNS,
  EXCEL_FILE_NAME,
  FILTER_IDS,
} from './building-scan.constants';
import { SharedModule } from '../../shared/shared.module';

@Component({
  selector: 'app-building-scan',
  templateUrl: './building-scan.component.html',
  styleUrls: ['./building-scan.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    FormsModule,
    ReactiveFormsModule,
    MatTableModule,
    MatCheckboxModule,
    MatIconModule,
    MatSortModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    ScrollingModule,
    NgSelectModule,
    IonRow,
    IonCheckbox,
  ],
})
export class BuildingScanComponent implements OnInit, OnDestroy {
  readonly COLUMNS = COLUMN_TEXT;
  readonly CONSTS = CONSTS;
  readonly IMAGES = IMAGE_SRC;
  readonly FILTER_IDS = FILTER_IDS;
  readonly COLUMN_IDS = ColumnIds;
  readonly SPECIAL_CHARS = SpecialCharacters;

  descriptionData = this.commonUtils.createTableColumn(ColumnIds.DESCRIPTION);
  reasonData = this.commonUtils.createTableColumn(ColumnIds.REASON);
  statusData = this.commonUtils.createTableColumn(ColumnIds.STATUS);
  resolutionData = this.commonUtils.createTableColumn(ColumnIds.RESOLUTION);
  enabledData = this.commonUtils.createTableColumn(ColumnIds.ENABLED);

  dataSource = new MatTableDataSource<BuildingScanAdmin>();
  selectedFilter = FILTER_IDS.SITE;
  showMobileFilters = false;
  originalTableData!: BuildingScanAdmin[];
  destroy = inject(DestroyRef);
  private readonly dataService = inject(DataService);

  currentUser!: UserAdmin;

  constructor(
    private readonly commonUtils: CommonUtils,
    private readonly buildingScan: BuildingScanService,
    private readonly userService: UserService,
  ) {}

  ngOnInit(): void {
    this.dataService.setPageHeader(CONSTS.HEADER);

    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        filter((user): user is UserAdmin => !!user),
        switchMap((user) => {
          return this.buildingScan.getAllExceptionCodes(user?.location).pipe(
            takeUntilDestroyed(this.destroy),
            map((values) => {
              this.dataSource.data = values?.response?.exceptionCodes;
              //Deep copy original data so enable field can be compared when changing value
              this.originalTableData = JSON.parse(
                JSON.stringify(values?.response?.exceptionCodes),
              );
              this.dataSource.filterPredicate = this.commonUtils.createFilter();
              this.currentUser = user;
            }),
          );
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.dataService.setPageHeader('');
  }

  clearAllFilters() {
    this.commonUtils.clearAllFilters(this.dataSource, this.originalTableData, [
      this.descriptionData.formControl,
      this.reasonData.formControl,
      this.statusData.formControl,
      this.resolutionData.formControl,
      this.enabledData.formControl,
    ]);
  }

  exportExcel() {
    this.commonUtils.exportExcel(
      EXCEL_COLUMNS,
      EXCEL_FILE_NAME,
      this.dataSource.filteredData,
      ['id', 'displayOrder', 'allowFutureDate'],
    );
  }

  radioGroupValue(event: Event) {
    this.selectedFilter = (event?.target as HTMLElement)?.id;
  }

  searchTypeChange() {
    this.search();
  }

  toggleMobileFilters() {
    this.showMobileFilters = !this.showMobileFilters;
  }

  applyMobileFilters() {
    this.search();
    this.showMobileFilters = false;
  }

  clearMobileFilters() {
    this.clearAllFilters();
    this.showMobileFilters = false;
  }

  checkboxValueChanges(record: BuildingScanAdmin, newValue: boolean) {
    const locationText =
      this.selectedFilter === FILTER_IDS.SITE
        ? CONSTS.SITE.toLowerCase()
        : CONSTS.GROUP.toLowerCase();

    const headerText = newValue
      ? CONSTS.WARNING_MODAL.ENABLE.HEADER
      : CONSTS.WARNING_MODAL.DISABLE.HEADER;

    const bodyText = newValue
      ? CONSTS.WARNING_MODAL.ENABLE.BODY.replace('%s', locationText)
      : CONSTS.WARNING_MODAL.DISABLE.BODY.replace('%s', locationText);

    this.commonUtils
      .openWarningModal({
        header: headerText,
        body: bodyText,
        extraText: record.description,
        extraClass: 'building-scan-warning',
      })
      .afterClosed()
      .subscribe((resp) => {
        if (resp.button === BUTTON_CLICK.ACCEPT) {
          const enableDisable = {
            site: this.selectedFilter === FILTER_IDS.SITE,
            group: this.selectedFilter === FILTER_IDS.GROUP,
            description: record.description,
            enableSite:
              this.selectedFilter === FILTER_IDS.SITE ? record.enable : false,
            enableGroup:
              this.selectedFilter === FILTER_IDS.GROUP ? record.enable : false,
          };
          this.updateData([enableDisable]);
        } else {
          record.enable = !newValue;
        }
      });
  }

  search() {
    this.commonUtils.searchFilter(
      [
        this.descriptionData,
        this.reasonData,
        this.statusData,
        this.resolutionData,
      ],
      this.dataSource,
      [this.enabledData],
    );
  }

  get isFilterEnabled() {
    return (
      this.descriptionData.formControl.value !== '' ||
      this.reasonData.formControl.value !== '' ||
      this.statusData.formControl.value !== '' ||
      this.resolutionData.formControl.value !== ''
    );
  }

  get activeFilterCount() {
    return [
      this.descriptionData.formControl.value,
      this.reasonData.formControl.value,
      this.statusData.formControl.value,
      this.resolutionData.formControl.value,
    ].filter((value) => (value ?? '').toString().trim().length > 0).length;
  }

  get mobileTotalItems() {
    return this.dataSource.filteredData.length;
  }

  get mobileVisibleData() {
    return this.dataSource.filteredData;
  }

  private updateData(changedData: EnableDisable[]) {
    this.buildingScan
      .enableDisableExceptions(
        { exceptionCodes: changedData },
        this.currentUser.location,
        this.currentUser.locationGroup,
      )
      .pipe(take(1))
      .subscribe(() => {
        this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
      });
  }
}
